import { useState, useMemo } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import {
  ComposedChart, Area, Line, XAxis, YAxis,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { NET_WORTH_DATA, fmt } from "../../data/mockData";

// ─── Starting financial position (from mockData) ──────────────────────────────
const INVESTMENTS_NOW = 180841;
const CASH_NOW        =  42631; // savings + checking (stays flat in model)
const DEBT_NOW        =  21240; // student loan + credit card

const CURRENT_YEAR = new Date().getFullYear();

// ─── Projection engine ────────────────────────────────────────────────────────
function project({ annualContribution, returnRate, homeAppreciation, debtRate, annualDebtPayment, propertyValue, horizon }) {
  const results = [];
  let inv  = INVESTMENTS_NOW;
  let prop = propertyValue;
  let debt = DEBT_NOW;

  for (let y = 0; y <= horizon; y++) {
    results.push({
      year: y === 0 ? "Now" : String(CURRENT_YEAR + y),
      nw: Math.round(inv + prop + CASH_NOW - debt),
    });
    inv  = inv  * (1 + returnRate       / 100) + annualContribution;
    prop = prop * (1 + homeAppreciation / 100);
    debt = Math.max(0, debt * (1 + debtRate / 100) - annualDebtPayment);
  }
  return results;
}

// ─── Custom tooltip ───────────────────────────────────────────────────────────
const ProjTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-strong)",
      borderRadius: "10px", padding: "12px 16px",
    }}>
      <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginBottom: "8px" }}>{label}</div>
      {payload.map((p, i) => p.value != null && (
        <div key={i} style={{ fontSize: "13px", color: p.color, fontFamily: "'JetBrains Mono', monospace" }}>
          {p.name}: {fmt(p.value)}
        </div>
      ))}
    </div>
  );
};

// ─── Param slider ─────────────────────────────────────────────────────────────
const Slider = ({ label, value, min, max, step, format, onChange }) => (
  <div style={{ marginBottom: "18px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
      <span style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</span>
      <span style={{ fontSize: "12px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", fontWeight: "600" }}>{format(value)}</span>
    </div>
    <div style={{ position: "relative" }}>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: "#D4AF37", cursor: "pointer", height: "4px" }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "3px" }}>
        <span style={{ fontSize: "9px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>{format(min)}</span>
        <span style={{ fontSize: "9px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>{format(max)}</span>
      </div>
    </div>
  </div>
);

const HorizonBtn = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    flex: 1, padding: "6px 0", borderRadius: "6px",
    background: active ? "var(--bg-hover)" : "transparent",
    border: active ? "1px solid var(--border-active)" : "1px solid transparent",
    fontSize: "11px", fontFamily: "'JetBrains Mono', monospace",
    color: active ? "#D4AF37" : "var(--text-faint)",
    cursor: "pointer", transition: "all 0.15s",
    fontWeight: active ? "600" : "normal",
  }}>{label}</button>
);

const fmtK  = v => v >= 1e6 ? `$${(v/1e6).toFixed(2)}M` : `$${(v/1e3).toFixed(0)}K`;
const fmtPct = v => `${v}%`;

// ─── Main component ───────────────────────────────────────────────────────────
export default function NetWorthProjection() {
  const isMobile = useIsMobile();
  const [annualContribution, setAnnualContribution] = useState(12000);
  const [returnRate,         setReturnRate]         = useState(7);
  const [homeAppreciation,   setHomeAppreciation]   = useState(3);
  const [debtRate,           setDebtRate]           = useState(5);
  const [annualDebtPayment,  setAnnualDebtPayment]  = useState(6000);
  const [propertyValue,      setPropertyValue]      = useState(0);
  const [horizon,            setHorizon]            = useState(10);
  const [showOptimistic,     setShowOptimistic]     = useState(true);

  // Build chart data: historical (solid) + 3 projection scenarios (base, opt, pess)
  const chartData = useMemo(() => {
    const base = project({ annualContribution, returnRate,     homeAppreciation, debtRate, annualDebtPayment, propertyValue, horizon });
    const opt  = project({ annualContribution, returnRate: returnRate + 2, homeAppreciation, debtRate, annualDebtPayment, propertyValue, horizon });
    const pess = project({ annualContribution, returnRate: returnRate - 2, homeAppreciation, debtRate, annualDebtPayment, propertyValue, horizon });

    // Historical points (null for projected fields)
    const historical = NET_WORTH_DATA.map(d => ({
      label: d.month, historical: d.value,
      base: undefined, opt: undefined, pess: undefined,
    }));

    // Projection points (null for historical), starting at "Now"
    const projected = base.map((p, i) => ({
      label: p.year,
      historical: i === 0 ? p.nw : undefined, // connect lines at Now
      base: p.nw,
      opt:  opt[i].nw,
      pess: pess[i].nw,
    }));

    return [...historical, ...projected];
  }, [annualContribution, returnRate, homeAppreciation, debtRate, annualDebtPayment, propertyValue, horizon]);

  // Summary stats
  const baseEnd  = chartData[chartData.length - 1].base;
  const pessEnd  = chartData[chartData.length - 1].pess;
  const optEnd   = chartData[chartData.length - 1].opt;
  const currentNW = 202231;
  const totalGain = baseEnd - currentNW;
  const gainPct   = ((totalGain / currentNW) * 100).toFixed(0);

  const HORIZONS = [
    { label: "5Y",  value: 5  },
    { label: "10Y", value: 10 },
    { label: "20Y", value: 20 },
    { label: "30Y", value: 30 },
  ];

  return (
    <div style={{
      background: "var(--bg-surface)", border: "1px solid var(--border)",
      borderRadius: "16px", padding: "24px", marginBottom: "24px",
    }}>
      {/* Header */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "flex-start", gap: isMobile ? "12px" : "0", marginBottom: "20px" }}>
        <div>
          <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "4px" }}>
            Net Worth Projection
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
            Historical data + model-based forecast · adjust parameters to explore scenarios
          </div>
        </div>
        {/* Projected value badge */}
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "2px" }}>
            In {horizon} years (base)
          </div>
          <div style={{ fontSize: "22px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: "#52D68A" }}>
            {fmtK(baseEnd)}
          </div>
          <div style={{ fontSize: "11px", color: "#52D68A", fontFamily: "'JetBrains Mono', monospace" }}>
            +{fmtK(totalGain)} (+{gainPct}%)
          </div>
        </div>
      </div>

      {/* Main layout: chart left, params right */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 260px", gap: "28px", alignItems: "start" }}>

        {/* ── Chart ─────────────────────────────────────── */}
        <div>
          {/* Horizon selector */}
          <div style={{
            display: "flex", gap: "4px", marginBottom: "16px",
            background: "var(--bg-main)", borderRadius: "8px", padding: "3px",
            border: "1px solid var(--border)", width: "fit-content",
          }}>
            {HORIZONS.map(h => (
              <HorizonBtn key={h.value} label={h.label} active={horizon === h.value} onClick={() => setHorizon(h.value)} />
            ))}
          </div>

          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="histGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#D4AF37" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="baseGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#52D68A" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#52D68A" stopOpacity={0} />
                </linearGradient>
              </defs>

              <XAxis
                dataKey="label"
                tick={{ fill: "var(--text-faint)", fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }}
                axisLine={false} tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                hide domain={["auto", "auto"]}
                tickFormatter={v => fmtK(v)}
              />
              <Tooltip content={<ProjTooltip />} />

              {/* "Now" divider */}
              <ReferenceLine
                x="Now"
                stroke="var(--border-strong)"
                strokeDasharray="4 4"
                label={{ value: "TODAY", position: "top", fill: "var(--text-muted)", fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }}
              />

              {/* Historical area */}
              <Area
                type="monotone" dataKey="historical" name="Historical"
                stroke="#D4AF37" strokeWidth={2}
                fill="url(#histGrad)"
                dot={false} connectNulls={false}
                legendType="none"
              />

              {/* Base projection area + line */}
              <Area
                type="monotone" dataKey="base" name="Base Projection"
                stroke="#52D68A" strokeWidth={2.5}
                strokeDasharray="8 4"
                fill="url(#baseGrad)"
                dot={false} connectNulls={false}
              />

              {/* Optimistic scenario */}
              {showOptimistic && (
                <Line
                  type="monotone" dataKey="opt" name={`Optimistic (+2%)`}
                  stroke="#34C472" strokeWidth={1}
                  strokeDasharray="3 4"
                  dot={false} connectNulls={false}
                />
              )}

              {/* Pessimistic scenario */}
              {showOptimistic && (
                <Line
                  type="monotone" dataKey="pess" name={`Pessimistic (−2%)`}
                  stroke="#F87171" strokeWidth={1}
                  strokeDasharray="3 4"
                  dot={false} connectNulls={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>

          {/* Scenario summary row */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginTop: "16px" }}>
            {[
              { label: "Pessimistic (−2%)", value: pessEnd, color: "#F87171" },
              { label: "Base",              value: baseEnd, color: "#52D68A" },
              { label: "Optimistic (+2%)", value: optEnd,  color: "#34C472" },
            ].map(s => (
              <div key={s.label} style={{
                flex: 1, background: "var(--bg-main)", borderRadius: "10px",
                padding: "12px 14px", border: "1px solid var(--border)",
              }}>
                <div style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "4px" }}>{s.label}</div>
                <div style={{ fontSize: "16px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: s.color }}>{fmtK(s.value)}</div>
                <div style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginTop: "2px" }}>
                  +{fmtK(s.value - currentNW)}
                </div>
              </div>
            ))}
          </div>

          {/* Scenario lines toggle */}
          <div style={{ marginTop: "12px" }}>
            <button
              onClick={() => setShowOptimistic(p => !p)}
              style={{
                background: "none", border: "1px solid var(--border-strong)", borderRadius: "6px",
                padding: "5px 12px", fontSize: "11px", color: showOptimistic ? "#D4AF37" : "#444",
                fontFamily: "'JetBrains Mono', monospace", cursor: "pointer",
              }}
            >
              {showOptimistic ? "Hide" : "Show"} optimistic / pessimistic bands
            </button>
          </div>
        </div>

        {/* ── Parameters panel ──────────────────────────── */}
        <div style={{ background: "var(--bg-main)", borderRadius: "12px", padding: "20px", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "18px" }}>
            Model Parameters
          </div>

          <Slider
            label="Annual Contribution"
            value={annualContribution} min={0} max={50000} step={500}
            format={v => `$${(v/1000).toFixed(1)}K/yr`}
            onChange={setAnnualContribution}
          />
          <Slider
            label="Investment Return"
            value={returnRate} min={1} max={15} step={0.5}
            format={fmtPct}
            onChange={setReturnRate}
          />
          <Slider
            label="Home Appreciation"
            value={homeAppreciation} min={0} max={10} step={0.5}
            format={fmtPct}
            onChange={setHomeAppreciation}
          />
          <Slider
            label="Property Value"
            value={propertyValue} min={0} max={1000000} step={25000}
            format={v => v === 0 ? "None" : fmtK(v)}
            onChange={setPropertyValue}
          />
          <Slider
            label="Debt Interest Rate"
            value={debtRate} min={0} max={20} step={0.25}
            format={fmtPct}
            onChange={setDebtRate}
          />
          <Slider
            label="Annual Debt Payment"
            value={annualDebtPayment} min={0} max={24000} step={600}
            format={v => `$${(v/1000).toFixed(1)}K/yr`}
            onChange={setAnnualDebtPayment}
          />

          {/* Assumptions note */}
          <div style={{
            marginTop: "8px", padding: "12px",
            background: "var(--bg-surface)", borderRadius: "8px", border: "1px solid var(--border)",
            fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.6,
          }}>
            Starting position:<br />
            Investments: {fmt(INVESTMENTS_NOW)}<br />
            Cash/Savings: {fmt(CASH_NOW)}<br />
            Debt: {fmt(DEBT_NOW)}<br />
            {propertyValue > 0 && <>Property: {fmtK(propertyValue)}<br /></>}
            ± 2% return for scenarios
          </div>
        </div>
      </div>
    </div>
  );
}
